import { useRef } from 'react';
import { useImageStore } from '@/entities/image/store';
import { isElectron } from '@/shared/platform';

interface ToolbarProps {
  onOpenFolder: () => void;
  onApply: () => void;
  folderPath: string | null;
}

export default function Toolbar({ onOpenFolder, onApply, folderPath }: ToolbarProps) {
  const searchQuery = useImageStore((s) => s.searchQuery);
  const setSearchQuery = useImageStore((s) => s.setSearchQuery);
  const showLLMPanel = useImageStore((s) => s.showLLMPanel);
  const setShowLLMPanel = useImageStore((s) => s.setShowLLMPanel);
  const showDiffPreview = useImageStore((s) => s.showDiffPreview);
  const setShowDiffPreview = useImageStore((s) => s.setShowDiffPreview);
  const selectedCount = useImageStore((s) => s.selectedIndices.size);
  const changeCount = useImageStore((s) => s.stagedChanges.size);
  const uploadFiles = useImageStore((s) => s.uploadFiles);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadFiles(files);
      // Reset the input so the same files can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleOpenButtonClick = () => {
    if (isElectron) {
      onOpenFolder();
    } else {
      // Web mode - trigger file input
      fileInputRef.current?.click();
    }
  };

  return (
    <header className="flex items-center gap-3 px-4 py-2 bg-gray-800 border-b border-gray-700">
      {/* Hidden file input for web mode */}
      {!isElectron && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.tiff,.tif,.webp,.bmp,.gif,image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      )}

      <button
        onClick={handleOpenButtonClick}
        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium transition"
      >
        {isElectron ? 'Open Folder' : 'Open Files'}
      </button>

      <span className="text-xs text-gray-400 truncate max-w-xs">
        {folderPath || 'No folder selected'}
      </span>

      <input
        type="text"
        placeholder="Search images..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="flex-1 px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />

      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowLLMPanel(!showLLMPanel)}
          className={`px-2 py-1.5 text-sm rounded transition ${showLLMPanel ? 'bg-purple-600 text-white' : 'bg-gray-700 hover:bg-gray-600'}`}
        >
          AI
        </button>
        <button
          onClick={() => setShowDiffPreview(!showDiffPreview)}
          className={`px-2 py-1.5 text-sm rounded transition ${showDiffPreview ? 'bg-yellow-600 text-white' : 'bg-gray-700 hover:bg-gray-600'}`}
        >
          Diff
        </button>
        {selectedCount > 0 && (
          <span className="text-xs text-blue-400">{selectedCount} selected</span>
        )}
        <button
          onClick={onApply}
          disabled={changeCount === 0}
          className={`px-3 py-1.5 rounded text-sm font-medium transition ${
            changeCount === 0
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {changeCount > 0 ? `Apply (${changeCount})` : 'Apply'}
        </button>
      </div>
    </header>
  );
}
